import { useDuplicateTirage } from "@/api/tirages/tirage.api";
import {
  appendNoDuplicateRecordNumberRefine,
  tirageSchema,
} from "@/api/tirages/tirage.validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import DuplicateEntityDialog from "../modal/DuplicateEntityDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

const schema = appendNoDuplicateRecordNumberRefine(
  tirageSchema.pick({
    tirageDescriptionNumeroEnregistrement: true,
  })
);

type FormType = z.infer<typeof schema>;

type DuplicateTirageDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceTirageId: string | undefined;
  artworkId: string;
  onDuplicateSuccess: (created: {
    id: string;
    tirageDescriptionNumeroEnregistrement?: string;
  }) => void;
};

const DuplicateTirageDialog = ({
  open,
  onOpenChange,
  sourceTirageId,
  artworkId,
  onDuplicateSuccess,
}: DuplicateTirageDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      tirageDescriptionNumeroEnregistrement: "",
    },
  });
  const { mutateAsync, isPending } = useDuplicateTirage();

  const handleSubmit = async (data: FormType) => {
    if (!sourceTirageId) return;
    try {
      const created = await mutateAsync({
        sourceId: sourceTirageId,
        artworkId,
        tirageDescriptionNumeroEnregistrement:
          data.tirageDescriptionNumeroEnregistrement,
      });
      onDuplicateSuccess({
        id: created.id,
        tirageDescriptionNumeroEnregistrement:
          created.tirageDescriptionNumeroEnregistrement,
      });
      onOpenChange(false);
      form.reset();
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("tirageDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <DuplicateEntityDialog<FormType>
      open={open}
      onOpenChange={onOpenChange}
      title={t("duplicateTirage")}
      form={form}
      onSubmit={handleSubmit}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="tirageDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("tirageDescriptionNumeroEnregistrement")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </DuplicateEntityDialog>
  );
};

export default DuplicateTirageDialog;
